import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import * as cfFtpInfoSelectors from 'data/cfFtpInfo/cfFtpInfo.selectors';
import { RootState } from 'data/root.reducers';
import * as cfFtpInfoActions from 'data/cfFtpInfo/cfFtpInfo.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { CfFtpInfo } from 'data/cfFtpInfo/cfFtpInfo.types';
import CfFtpInfoServerForm from 'routes/CfFtpInfo/components/CfFtpInfoServerForm';
import * as types from 'data/cfFtpInfo/cfFtpInfo.types';
import { cfFtpInfoTemplate } from '../../templates';
import { adaptTemplateVisibilityMode } from '../../utils/cfFtpInfoTemplate.adapter';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.CfFtpInfo
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedCfFtpInfo: cfFtpInfoSelectors
    .getSelectedCfFtpInfo(state)[0],
  cfFtpInfo: cfFtpInfoSelectors.getCfFtpInfo(state),
});

const mapDispatch = {
  goToCfFtpInfoList: cfFtpInfoActions.goToCfFtpInfoList,
  goToCfFtpInfoEdit: cfFtpInfoActions.goToCfFtpInfoEdit,
  getCfFtpInfo: cfFtpInfoActions.getCfFtpInfo,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const CfFtpInfoView = ({
  history,
  selectedCfFtpInfo,
  deliveryFunctionId,
  cfFtpInfo,
  goToCfFtpInfoList,
  goToCfFtpInfoEdit,
  getCfFtpInfo,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<CfFtpInfo>>({});
  const [template, setTemplate] = useState(adaptTemplateVisibilityMode(cfFtpInfoTemplate, 'view'));
  const { t } = useTranslation();

  const id = selectedCfFtpInfo?.id;

  useEffect(() => {
    if (id) {
      getCfFtpInfo(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getCfFtpInfo]);

  useEffect(() => {
    if (cfFtpInfo != null && template != null) {
      const clonedCfFtpInfo = cloneDeep(cfFtpInfo);
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      setTemplate(clonedTemplate);
      setFormData(clonedCfFtpInfo);
    }
  }, [cfFtpInfo, id]);

  const handleClickEdit = () => goToCfFtpInfoEdit(history);

  const handleCancel = () => goToCfFtpInfoList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as CfFtpInfo);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <CfFtpInfoServerForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleClickEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (cfFtpInfo == null) {
    return <Fragment />;
  }

  return (
    <div className="read-only" data-testid="CfFtpInfoView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('cfFtpInfo.view.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(CfFtpInfoView));
